import type { ForwardedRef } from 'react';
import type { Button } from '@progress/kendo-react-buttons';
import type { Dialog, DialogProps } from '@progress/kendo-react-dialogs';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { IO_KEYBOARD_KEY } from '@ricoh/ria.client.core/constants/core/io';
import { useEvent } from '@ricoh/ria.client.core/hooks/app/event';
import { useRefList } from '@ricoh/ria.client.core/hooks/app/ref';
import { isNotNull, isNull } from '@ricoh/ria.client.core/utils/data/object';

import type { DialogActionButton, DialogActionHooks, DialogActionProps, DialogActionRef } from './_.type';

/**
 * Use DialogAction hooks.
 *
 * @param {DialogActionProps} props DialogAction props.
 * @param {DialogActionRef} ref DialogAction ref.
 *
 * @returns {DialogActionHooks} DialogAction hooks.
 */
export default function useDialogActionHooks(
  { state: [show, setShow], actionButtons }: DialogActionProps,
  ref: ForwardedRef<DialogActionRef>,
): DialogActionHooks {
  const dialogRef = useRef<Dialog>(null);
  const buttonsRef = useRefList<Button>([]);

  const createDialogActionButton = useCallback<Func<[actionButton: DialogActionButton], DialogActionButton>>(
    (actionButton) => {
      const onClick = actionButton.buttonProps?.onClick;

      actionButton.buttonProps = {
        ...actionButton.buttonProps,
        onClick: (event) => {
          onClick?.(event);
          setShow(() => false);
        },
      };

      return actionButton;
    },
    [setShow],
  );

  const buttons = useMemo<DialogActionButton[]>(() => actionButtons?.map(createDialogActionButton) ?? [], [actionButtons, createDialogActionButton]);

  const buttonPrimaryIndex = useMemo<Nullable<number>>(() => {
    const index = buttons.findIndex((button) => 'primary' in button && button.primary);
    return index < 0 ? undefined : index;
  }, [buttons]);

  const buttonCancelIndex = useMemo<Nullable<number>>(() => {
    const index = buttons.findIndex((button) => 'cancel' in button && button.cancel);
    return index < 0 ? undefined : index;
  }, [buttons]);

  const onKeydown = useCallback<Func<[event: KeyboardEvent]>>(
    ({ key }) => {
      const buttonIndex = {
        [IO_KEYBOARD_KEY.ENTER]: buttonPrimaryIndex,
        [IO_KEYBOARD_KEY.ESCAPE]: buttonCancelIndex,
      }[key];
      if (isNull(buttonIndex)) {
        return;
      }

      const buttonEl = buttonsRef.current.at(buttonIndex)?.element;
      if (!buttonEl) {
        return;
      }

      buttonEl.click();
    },
    [buttonCancelIndex, buttonPrimaryIndex, buttonsRef],
  );

  const cancel = useCallback<Func>(() => {
    if (isNull(buttonCancelIndex)) {
      setShow(() => false);
      return;
    }

    const buttonCancelEl = buttonsRef.current.at(buttonCancelIndex)?.element;
    if (!buttonCancelEl) {
      return;
    }

    buttonCancelEl.click();
  }, [buttonCancelIndex, buttonsRef, setShow]);

  const onCloseDialog = useCallback<NonNullable<DialogProps['onClose']>>(() => {
    cancel();
  }, [cancel]);

  useEffect(() => {
    const dialogEl = dialogRef.current?.element;
    if (!dialogEl) {
      return;
    }

    if (show) {
      dialogEl.focus();
    }
  }, [show]);

  useEvent({
    type: 'keydown',
    ref: dialogRef,
    selector: ({ current }) => current?.element,
    handle: onKeydown,
    shouldBind: show,
  });

  useImperativeHandle(ref, () => ({}));

  return useMemo<DialogActionHooks>(
    () => ({
      refs: {
        dialogRef,
        buttonsRef,
      },

      states: {
        buttons,

        showCloseButton: isNotNull(buttonCancelIndex),
      },

      events: {
        onCloseDialog,
      },
    }),
    [buttonCancelIndex, buttons, buttonsRef, onCloseDialog],
  );
}
